/**
 * NodeTypeQuickPicker — compact categorised tile grid for picking the next
 * node type when adding a child in the list-builder.
 *
 * Differs from the single-line <Select> in CreateNodePopover:
 *   - Tiles are two-line (label + short hint) and group by semantic
 *     category.
 *   - Respects feature-flag gating the same way CreateNodePopover does so
 *     e.g. a reseller without the `conference` flag never sees conference
 *     as an option.
 *   - Supports an optional `filter` prop (regex/substring on label/type)
 *     so the caller can implement a search box.
 *   - Emits the chosen type via `onPick`; never mutates state itself.
 */
import { useMemo, useState } from 'react';
import type { IvrNodeType } from '@/api/ivrNodes';
import { IVR_NODE_TYPE_GROUPS } from '@/api/ivrNodes';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { cn } from '@/lib/cn';
import { Search } from 'lucide-react';

/** Node types gated behind feature flags. Matches CreateNodePopover. */
const NODE_TYPE_FEATURE_MAP: Partial<Record<IvrNodeType, string>> = {
  voicemail: 'voicemail',
  listen_voicemail: 'voicemail',
  conference: 'conference',
  survey: 'surveys',
  quiz: 'quizzes',
  enrollment: 'courses',
  shopping: 'shop',
  points: 'points',
  public_forum: 'public_forum',
  ring_group: 'ring_groups',
  payment: 'payments',
  extension: 'extensions',
};

interface Props {
  /**
   * Optional allow-list. If provided, only these types are shown. Useful
   * for contextual pickers (e.g. "next-node only" for a playback's
   * default successor slot).
   */
  allowedTypes?: ReadonlyArray<IvrNodeType>;
  /** Current selection — rendered with a highlighted ring. */
  value?: IvrNodeType;
  onPick: (type: IvrNodeType) => void;
  /** Optional className on the outer container. */
  className?: string;
  /**
   * When true the picker stretches to its parent's height and the tile
   * grid becomes the ONLY scroll region (flex-1) instead of a fixed
   * max-h — used by AddChildPopover's two-column layout so the dialog
   * never nests two scrollbars.
   */
  fillHeight?: boolean;
}

export function NodeTypeQuickPicker({ allowedTypes, value, onPick, className, fillHeight }: Props) {
  const { isEnabled } = useFeatureFlag();
  const [q, setQ] = useState('');

  const allowedSet = useMemo(
    () => (allowedTypes ? new Set(allowedTypes) : null),
    [allowedTypes],
  );

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return IVR_NODE_TYPE_GROUPS
      .map((g) => ({
        ...g,
        types: g.types.filter((t) => {
          if (allowedSet && !allowedSet.has(t.type)) return false;
          const flag = NODE_TYPE_FEATURE_MAP[t.type];
          if (flag && !isEnabled(flag)) return false;
          if (!needle) return true;
          return (
            t.label.toLowerCase().includes(needle) ||
            t.type.toLowerCase().includes(needle) ||
            t.hint.toLowerCase().includes(needle)
          );
        }),
      }))
      .filter((g) => g.types.length > 0);
  }, [q, allowedSet, isEnabled]);

  const empty = groups.length === 0;

  return (
    <div className={cn('flex flex-col gap-2', fillHeight && 'h-full min-h-0', className)}>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search node types…"
          aria-label="Search node types"
          className={cn(
            'w-full h-8 rounded-md border border-slate-200 bg-white pl-8 pr-2 text-sm',
            'placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400',
          )}
        />
      </div>

      {/* Tile grid, scrollable */}
      <div
        className={cn(
          'overflow-y-auto pe-1 -me-1 space-y-3',
          fillHeight ? 'flex-1 min-h-0' : 'max-h-[360px]',
        )}
      >
        {empty && (
          <div className="text-xs text-slate-400 italic py-8 text-center">
            No node types match.
          </div>
        )}
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {group.label}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {group.types.map((t) => {
                const isActive = value === t.type;
                return (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => onPick(t.type)}
                    className={cn(
                      'group text-left rounded-md border px-2.5 py-1.5 transition-colors',
                      'hover:bg-indigo-50/40 hover:border-indigo-200',
                      'focus:outline-none focus:ring-2 focus:ring-indigo-400/40',
                      isActive
                        ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
                        : 'border-slate-200 bg-white',
                    )}
                    title={t.hint}
                    aria-pressed={isActive}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-slate-800 truncate">
                        {t.label}
                      </span>
                      <span className="shrink-0 font-mono text-[9px] text-slate-400">
                        {t.type}
                      </span>
                    </div>
                    <p className="text-[10.5px] text-slate-500 leading-snug truncate">
                      {t.hint}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
